import { useEffect, useRef, useState } from 'react';
import './SalaryAccountStep.css';
const API = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
function accountSuffix(account) {
  const mask = String(account?.maskedAccNumber || account?.masskedAccNumber || '').trim();
  return mask.match(/\d{4}$/)?.[0];
}
function validateAccountNumber(account, number) {
  if (!account) return 'Please select your salary account.';
  if (!number) return 'Please enter your full account number.';
  if (!/^\d{6,20}$/.test(number)) return 'Account number must contain 6–20 digits only.';
  const suffix = accountSuffix(account);
  if (!suffix) return 'This account has no visible digits to verify. Please select another account or run AA again.';
  if (!number.endsWith(suffix)) return `Account number must end in ${suffix} to match the selected account.`;
  return '';
}
export default function SalaryAccountStep({ data, onChange, onRestartAA }) {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState('');
  const [number, setNumber] = useState('');
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState('');
  const [retry, setRetry] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(30);
  const [showCounter, setShowCounter] = useState(false);
  const [statement, setStatement] = useState(null);
  const [statementUrl, setStatementUrl] = useState(null);
  const [replacingStatement, setReplacingStatement] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [password, setPassword] = useState('');
  // Salary account typed in alongside an uploaded statement.
  const [statementAccount, setStatementAccount] = useState('');
  const [statementIfsc, setStatementIfsc] = useState('');
  // Whether a penny drop backs the account saved with the statement.
  const [statementVerified, setStatementVerified] = useState(false);
  // Account + number last sent for verification, so each entry is verified once.
  const attempted = useRef('');
  // Once the penny drop succeeds the account and its number are locked.
  const [verified, setVerified] = useState(false);
  // Penny-drop failure for the number as typed; shown on the field until it changes.
  const [verifyError, setVerifyError] = useState('');
  // Verified account that is not in the current AA list (e.g. after AA was run again).
  const [lockedAccount, setLockedAccount] = useState(null);
  const token = JSON.parse(localStorage.getItem('userSession') || '{}').token || '';
  useEffect(() => {
    let active = true;
    let pollTimer;
    const controller = new AbortController();
    onChange('salaryAccountDone', false);
    attempted.current = '';
    setVerified(false);
    setLockedAccount(null);
    setBusy(true);
    setTimedOut(false);
    setAccounts([]);
    setSelected('');
    setNumber('');
    setStatementUrl(null);
    setReplacingStatement(false);
    setStatementAccount('');
    setStatementIfsc('');
    setStatementVerified(false);
    setMessage('');
    setShowCounter(false);
    setSecondsRemaining(30);
    const expiresAt = Date.now() + 30000;
    const countdown = setInterval(() => {
      setSecondsRemaining(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
    }, 1000);
    const finish = (message = '') => {
      active = false;
      clearTimeout(pollTimer);
      clearTimeout(deadline);
      clearInterval(countdown);
      controller.abort();
      setBusy(false);
      setShowCounter(false);
      setMessage(message);
    };
    const deadline = setTimeout(() => {
      setTimedOut(true);
      finish('Your accounts are not available yet. Upload your bank statement or check again.');
    }, 30000);
    const checkAccounts = async () => {
      try {
        const response = await fetch(`${API}/api/signzy/getAAData`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        const result = await response.json();
        if (!active) return;
        if (response.status === 401 || response.status === 403) {
          finish(result.message || 'Please sign in again.');
          return;
        }
        if (response.ok && result.success) {
          if (result.bankStatementPdfUrl) {
            setStatementUrl(result.bankStatementPdfUrl);
            setStatementAccount(String(result.accountNumber || ''));
            setStatementIfsc(String(result.ifscNumber || ''));
            setStatementVerified(Boolean(result.accountVerified));
            onChange('salaryAccountDone', true);
            finish();
            return;
          }
          const parsed = typeof result.accounts === 'string' ? JSON.parse(result.accounts) : result.accounts;
          const list = Array.isArray(parsed) ? parsed : [];
          const savedNumber = String(result.accountNumber || '').trim();
          const matchingIndex = /^\d{6,20}$/.test(savedNumber)
            ? list.findIndex(item => accountSuffix(item) === savedNumber.slice(-4))
            : -1;
          if (result.accountVerified && savedNumber && matchingIndex === -1) {
            setAccounts(list);
            setLockedAccount({ number: savedNumber, ifsc: result.ifscNumber });
            setVerified(true);
            onChange('salaryAccountDone', true);
            finish();
            return;
          }
          if (list.length > 0) {
            setAccounts(list);
            if (matchingIndex !== -1 && result.accountVerified) {
              setSelected(String(matchingIndex));
              setNumber(savedNumber);
              attempted.current = `${matchingIndex}|${savedNumber}`;
              setVerified(true);
              onChange('salaryAccountDone', true);
            }
            finish();
            return;
          }
        }
      } catch (error) {
        if (!active) return;
      }
      if (active) {
        setShowCounter(true);
        setMessage('Please wait up to 30 seconds while we fetch your linked accounts.');
        pollTimer = setTimeout(checkAccounts, 3000);
      }
    };
    checkAccounts();
    return () => {
      active = false;
      clearTimeout(pollTimer);
      clearTimeout(deadline);
      clearInterval(countdown);
      controller.abort();
    };
  }, [token, onChange, retry]);
  const account = selected === '' ? null : accounts[Number(selected)];
  const mask = String(account?.maskedAccNumber || account?.masskedAccNumber || '');
  const suffix = accountSuffix(account);
  const validationError = validateAccountNumber(account, number);
  const matches = !validationError;
  const fieldError = validationError || verifyError;
  const save = async () => {
    if (busy || verified || data.salaryAccountDone) return;
    const error = validateAccountNumber(account, number);
    if (error) {
      setMessage(error);
      onChange('salaryAccountDone', false);
      return;
    }
    setBusy(true); setMessage(''); setVerifyError('');
    try {
      const response = await fetch(`${API}/api/signzy/updateAccountNumber`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ maskedAccNumber: mask, newAccNumber: number, ifscCode: account.IfscCode, fipName: account.fipName }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Unable to save account');
      setVerified(true);
      onChange('salaryAccountDone', true);
      setMessage('Bank account verified and saved. You can submit now.');
    } catch (error) { setVerifyError(error.message || 'Unable to save account'); }
    finally { setBusy(false); }
  };
  const attemptKey = `${selected}|${number}`;
  // Verify and save on its own once the full number matches the selected
  // account; the short wait lets the user finish typing first.
  useEffect(() => {
    if (busy || verified || validationError || data.salaryAccountDone || attempted.current === attemptKey) return undefined;
    const timer = setTimeout(() => { attempted.current = attemptKey; save(); }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptKey, validationError, busy, verified, data.salaryAccountDone]);
  const uploadStatement = async () => {
    if (busy || !statement) return;
    if (statement.type !== 'application/pdf' || !/\.pdf$/i.test(statement.name) || statement.size > 10 * 1024 * 1024) {
      setMessage('Please choose a PDF no larger than 10 MB.'); return;
    }
    const accountNumber = statementAccount.trim();
    const ifscNumber = statementIfsc.trim().toUpperCase();
    if (!/^\d{6,20}$/.test(accountNumber)) {
      setMessage('Account number must contain 6–20 digits only.'); return;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscNumber)) {
      setMessage('Please enter a valid 11-character IFSC code.'); return;
    }
    setBusy(true); setMessage('');
    setUploading(true);
    onChange('salaryAccountDone', false);
    const form = new FormData();
    form.append('password', password);
    form.append('accountNumber', accountNumber);
    form.append('ifscNumber', ifscNumber);
    form.append('document', statement);
    try {
      const response = await fetch(`${API}/api/signzy/upload/aa-statement`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Statement upload failed.');
      onChange('salaryAccountDone', true);
      if (result.bankStatementPdfUrl) setStatementUrl(result.bankStatementPdfUrl);
      setStatementAccount(accountNumber);
      setStatementIfsc(ifscNumber);
      setStatementVerified(true);
      setReplacingStatement(false);
      setStatement(null);
      setPassword('');
      // The statement card carries the confirmation once it has a link to show.
      setMessage(result.bankStatementPdfUrl ? '' : 'Bank account verified and statement uploaded. You can continue.');
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); setUploading(false); }
  };
  return <div className="alp-step-body salary-account-step">
    <h3>Select your salary account</h3>
    <p>Select the bank account where you receive your salary.</p>
    {busy && !uploading && !accounts.length && <div className="salary-account-loader" role="status">
      <span className="salary-account-ring" aria-hidden="true">
        <svg viewBox="0 0 48 48">
          <circle className="salary-account-ring-track" cx="24" cy="24" r="21" />
          <circle className="salary-account-ring-fill" cx="24" cy="24" r="21" pathLength="100"
            style={{ strokeDashoffset: showCounter ? 100 - (secondsRemaining / 30) * 100 : 75 }} />
        </svg>
        {showCounter && <span className="salary-account-ring-count">{secondsRemaining}</span>}
      </span>
      <span className="salary-account-loader-copy">
        <strong>Fetching your bank accounts…</strong>
        {showCounter && <span>{secondsRemaining} seconds remaining</span>}
        {message && <span>{message}</span>}
      </span>
    </div>}
    {busy && (uploading || accounts.length > 0) && <p className="salary-account-busy" role="status"><span className="salary-account-spinner" aria-hidden="true" />{uploading ? "Verifying your account and uploading statement…" : "Verifying your bank account…"}</p>}
    {statementUrl && <section className="salary-account-entry salary-account-verify is-valid">
      <span className="salary-account-verify-head">
        <span className="salary-account-verify-icon" aria-hidden="true">📄</span>
        <span className="salary-account-verify-copy">
          <span className="salary-account-verify-title">Your bank statement</span>
          <span className="salary-account-verify-sub">{/^\d{6,20}$/.test(statementAccount)
            ? `Account ending in ${statementAccount.slice(-4)}${statementIfsc ? ` · IFSC ${statementIfsc}` : ''}`
            : 'PDF statement for your salary account'}</span>
        </span>
      </span>
      {!replacingStatement && <p className="salary-account-verify-ok">✓ {statementVerified ? 'Bank account verified and statement saved' : 'Your statement is available'}. You can continue.</p>}
      <div className="salary-account-statement-actions">
        <a className="alp-btn-ghost" href={statementUrl} target="_blank" rel="noopener noreferrer">View bank statement</a>
        {!replacingStatement && <button type="button" className="alp-btn-ghost" disabled={busy} onClick={() => {
          setReplacingStatement(true); setStatement(null); setPassword(''); setMessage('');
          onChange('salaryAccountDone', false);
        }}>Update to latest statement</button>}
      </div>
    </section>}
    {!statementUrl && !busy && !lockedAccount && accounts.length === 0 && <button className="alp-btn-primary" onClick={() => setRetry(value => value + 1)}>Check again</button>}
    {!statementUrl && lockedAccount && <section className="salary-account-entry salary-account-verify is-valid">
      <span className="salary-account-verify-head">
        <span className="salary-account-verify-icon" aria-hidden="true">🔐</span>
        <span className="salary-account-verify-copy">
          <span className="salary-account-verify-title">Verified salary account</span>
          <span className="salary-account-verify-sub">Account ending in {lockedAccount.number.slice(-4)}{lockedAccount.ifsc ? ` · IFSC ${lockedAccount.ifsc}` : ''}</span>
        </span>
      </span>
      <p className="salary-account-verify-ok">✓ Bank account verified</p>
    </section>}
    {!statementUrl && !lockedAccount && accounts.length > 0 && <>
      <fieldset className="salary-account-options" disabled={busy || verified}>
        <legend>Your linked bank accounts</legend>
        {accounts.map((item, index) => <label key={index} className={`salary-account-option ${selected === String(index) ? 'is-selected' : ''}`}>
          <input type="radio" name="salary-account" value={index} checked={selected === String(index)} onChange={event => { setSelected(event.target.value); setNumber(''); setMessage(''); setVerifyError(''); onChange('salaryAccountDone', false); }} />
          <span className="salary-account-bank-icon" aria-hidden="true">🏦</span>
          <span className="salary-account-details">
            <strong>{item.fipName}</strong>
            <span className="salary-account-number">{item.maskedAccNumber || item.masskedAccNumber}</span>
            <span className="salary-account-ifsc">IFSC · {item.IfscCode || 'Unavailable'}</span>
          </span>
          {selected === String(index) && <span className="salary-account-badge">Selected</span>}
        </label>)}
      </fieldset>
      {account && <label className={`salary-account-entry salary-account-verify ${number ? (fieldError ? 'is-invalid' : 'is-valid') : ''}`}>
        <span className="salary-account-verify-head">
          <span className="salary-account-verify-icon" aria-hidden="true">🔐</span>
          <span className="salary-account-verify-copy">
            <span className="salary-account-verify-title">Full account number</span>
            <span className="salary-account-verify-sub">Confirm the account for {account.fipName}</span>
          </span>
        </span>
        <span className="salary-account-verify-field">
          <span className="salary-account-verify-prefix" aria-hidden="true">🏦</span>
          <input className="alp-input" inputMode="numeric" aria-invalid={Boolean(number && fieldError)} aria-describedby={number && fieldError ? 'salary-account-error' : undefined} value={number} disabled={busy || verified} onChange={event => { setNumber(event.target.value); setMessage(''); setVerifyError(''); onChange('salaryAccountDone', false); }} />
          {suffix && <span className="salary-account-verify-suffix" aria-hidden="true">••{suffix}</span>}
        </span>
        <p>Enter the full number ending in {suffix}.</p>
        {number && fieldError && <p id="salary-account-error" role="alert">{fieldError}</p>}
        {number && !fieldError && <p className="salary-account-verify-ok">{verified ? '✓ Bank account verified' : '✓ Matches the selected account'}</p>}
      </label>}
      {!busy && !verified && matches && !data.salaryAccountDone && attempted.current === attemptKey && <button className="alp-btn-primary" onClick={save}>Try again</button>}
    </>}
    {(replacingStatement || (!statementUrl && timedOut && accounts.length === 0)) && <section className="salary-account-upload">
      <div className="salary-account-upload-head">
        <span className="salary-account-upload-icon" aria-hidden="true">📄</span>
        <div>
          <h3>{replacingStatement ? 'Upload latest bank statement' : 'Upload your bank statement'}</h3>
          <p>{replacingStatement ? 'Choose the latest PDF statement to replace your saved bank statement.' : 'Accounts could not be fetched. Upload the PDF statement for your salary account.'}</p>
        </div>
      </div>
      <div className="alp-field">
        <label htmlFor="salary-statement-file">AA statement (PDF, up to 10 MB)</label>
        <label className={`salary-account-dropzone ${statement ? 'has-file' : ''} ${busy ? 'is-disabled' : ''}`}>
          <input id="salary-statement-file" className="salary-account-file-input" type="file" accept=".pdf,application/pdf" disabled={busy} onChange={event => { setStatement(event.target.files?.[0] || null); onChange('salaryAccountDone', false); }} />
          <span className="salary-account-dropzone-icon" aria-hidden="true">{statement ? '✅' : '⬆️'}</span>
          <span className="salary-account-dropzone-copy">
            <strong>{statement ? statement.name : 'Choose PDF statement'}</strong>
            <span>{statement ? 'Click to choose a different file' : 'PDF only · up to 10 MB'}</span>
          </span>
        </label>
      </div>
      <div className="alp-field">
        <label htmlFor="salary-statement-account">Account number</label>
        <input id="salary-statement-account" inputMode="numeric" placeholder="Enter salary account number" autoComplete="off" maxLength={20} value={statementAccount} disabled={busy} onChange={event => { setStatementAccount(event.target.value.replace(/\D/g, '')); setMessage(''); onChange('salaryAccountDone', false); }} />
      </div>
      <div className="alp-field">
        <label htmlFor="salary-statement-ifsc">IFSC code</label>
        <input id="salary-statement-ifsc" placeholder="e.g. HDFC0001234" autoComplete="off" autoCapitalize="characters" maxLength={11} value={statementIfsc} disabled={busy} onChange={event => { setStatementIfsc(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')); setMessage(''); onChange('salaryAccountDone', false); }} />
        <small className="salary-account-hint">Enter the account number and IFSC of the account in this statement. We verify the account before saving your statement.</small>
      </div>
      <div className="alp-field">
        <label htmlFor="salary-statement-password">PDF password (optional)</label>
        <input id="salary-statement-password" type="password" placeholder="Enter PDF password" autoComplete="new-password" maxLength={255} value={password} disabled={busy} onChange={event => { setPassword(event.target.value); onChange('salaryAccountDone', false); }} />
        <small className="salary-account-hint">Leave blank if the PDF is not password protected.</small>
      </div>
      <div className="salary-account-upload-actions">
        <button className="alp-btn-primary" disabled={busy || !statement || !statementAccount || !statementIfsc} onClick={uploadStatement}>{replacingStatement ? 'Upload latest statement' : 'Upload statement'}</button>
        {replacingStatement && <button type="button" className="alp-btn-ghost" disabled={busy} onClick={() => {
          setReplacingStatement(false); setStatement(null); setPassword(''); setMessage('');
          onChange('salaryAccountDone', true);
        }}>Cancel</button>}
      </div>
    </section>}
    {message && !(busy && !uploading && !accounts.length) && <p className="salary-account-message" role="status">{message}</p>}
    {!verified && <div className="salary-account-restart"><p>If your salary account is missing, run Account Aggregator again to link it.</p>
    <button className="alp-btn-ghost" disabled={busy} onClick={onRestartAA}>Run Account Aggregator again</button></div>}
  </div>;
}
